import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ErrorResponse } from "@core/types/errorResponse";
import type { PaginatedReportResponse, ReportHeaderResponse } from "./types";

export const REPORT_PAGE_ENDPOINTS = {
  HEADER: "report/header",
  BODY: "report/body",
} as const;

export const DEFAULT_PER_PAGE = 20;

export type ReportEntityArgs =
  | { scope: "church"; churchId: number; departmentId: number; reportTagId: number }
  | { scope: "district"; districtId: number; departmentId: number; reportTagId: number }
  | { scope: "nation"; nationId: number; departmentId: number; reportTagId: number };

export type FetchReportBodyArgs = ReportEntityArgs & {
  perPage?: number;
  /** Template origin for this tab. Church reports always send CHURCH. */
  origin: "CHURCH" | "DISTRICT" | "NATION";
  lensParams?: Record<string, string>;
  /** Page query for the active tab only (`page`, `churchPage`, `districtPage`, or `nationPage`). */
  pageParam?: { key: string; value: number };
  /** `default` or a presentation layout id. */
  layout?: string;
};

function buildPath(args: ReportEntityArgs, endpoint: string): string {
  const entityPath =
    args.scope === "church"
      ? `/church/${args.churchId}`
      : args.scope === "district"
        ? `/district/${args.districtId}`
        : `/nation/${args.nationId}`;
  return `${entityPath}/department/${args.departmentId}/${endpoint}`;
}

/** Report details, View options per section and the Layout list. */
export async function fetchReportHeaderAsync(
  args: ReportEntityArgs,
  signal?: AbortSignal
): Promise<ApiResponse<ReportHeaderResponse, ErrorResponse>> {
  const params = new URLSearchParams({ reportTagId: String(args.reportTagId) });
  return sendRequest<any, ReportHeaderResponse>(
    "GET",
    `${buildPath(args, REPORT_PAGE_ENDPOINTS.HEADER)}?${params}`,
    undefined,
    signal ? { signal } : undefined
  );
}

/** The default table (paged) or the rendered named layout. */
export async function fetchReportBodyAsync(
  args: FetchReportBodyArgs,
  signal?: AbortSignal
): Promise<ApiResponse<PaginatedReportResponse, ErrorResponse>> {
  const params: Record<string, string> = {
    reportTagId: String(args.reportTagId),
    perPage: String(args.perPage ?? DEFAULT_PER_PAGE),
    origin: args.origin,
    ...(args.lensParams ?? {}),
  };
  if (args.pageParam) {
    params[args.pageParam.key] = String(args.pageParam.value);
  }
  if (args.layout) {
    params.layout = args.layout;
  }

  return sendRequest<any, PaginatedReportResponse>(
    "GET",
    `${buildPath(args, REPORT_PAGE_ENDPOINTS.BODY)}?${new URLSearchParams(params)}`,
    undefined,
    signal ? { signal } : undefined
  );
}
